<template>
    <div class="space-y-6">
      <div class="flex flex-col gap-2">
        <h3 class="text-lg font-semibold text-gray-900">Documentos del Paciente</h3>
        <p class="text-sm text-gray-500">
          Gestiona los archivos cargados y visualiza su contenido sin salir de esta vista.
        </p>
      </div>

      <!-- Zona de carga de documentos -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 px-6 py-5 border-b">
          <div>
            <p class="text-sm font-medium text-gray-900">Subir nuevo documento</p>
            <p class="text-xs text-gray-500">Formatos permitidos: PDF, JPG, PNG, GIF, WebP.</p>
          </div>
          <div class="flex items-center gap-3">
            <p v-if="archivoSeleccionado" class="text-xs text-gray-600 truncate max-w-[200px]">
              {{ archivoSeleccionado.name }}
            </p>
            <button
              type="button"
              class="text-sm text-gray-600 hover:text-gray-900 underline"
              v-if="archivoSeleccionado"
              @click="limpiarArchivo"
              :disabled="subiendoDocumento"
            >
              Quitar
            </button>
          </div>
        </div>
        <div class="px-6 py-6">
          <label
            class="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition
                   hover:border-blue-400 hover:bg-blue-50 border-gray-300 bg-gray-50 text-center p-4"
          >
            <svg class="w-8 h-8 text-blue-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M7 16v-4a2 2 0 012-2h6a2 2 0 012 2v4m-2 4h-6a2 2 0 01-2-2v-4m6-4V4m0 0l-3 3m3-3l3 3" />
            </svg>
            <span class="text-sm font-medium text-gray-700">Selecciona o arrastra un archivo</span>
            <span class="text-xs text-gray-500 mt-1">Tamaño máximo recomendado: 10 MB</span>
            <input
              ref="archivoInput"
              type="file"
              class="hidden"
              @change="manejarArchivo"
              :disabled="subiendoDocumento"
            />
          </label>

          <div class="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              class="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!archivoSeleccionado || subiendoDocumento"
              @click="subirDocumento"
            >
              <svg
                v-if="subiendoDocumento"
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ subiendoDocumento ? "Subiendo..." : "Guardar documento" }}
            </button>
            <p
              v-if="mensajeSubida"
              :class="[
                'text-sm',
                tipoMensaje === 'error' ? 'text-red-600' : 'text-green-600'
              ]"
            >
              {{ mensajeSubida }}
            </p>
          </div>
        </div>
      </section>
  
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <p class="text-sm font-semibold text-gray-900">Biblioteca</p>
            <p class="text-xs text-gray-500">Haz clic en un documento para previsualizarlo.</p>
          </div>
          <span class="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1">
            {{ documentos.length }} documentos
          </span>
        </div>

        <div v-if="documentos.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <article
            v-for="doc in documentos"
            :key="doc.nombre"
            class="border border-gray-200 rounded-xl p-4 flex flex-col justify-between hover:border-blue-400 transition cursor-pointer bg-gray-50"
            @click="abrirModal(doc)"
          >
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 7v10a2 2 0 002 2h6a2 2 0 002-2V7m-8 0V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-800 truncate">{{ doc.nombre }}</p>
                <p class="text-xs text-gray-500 truncate">{{ obtenerExtension(doc.nombre) }}</p>
              </div>
            </div>
            <button class="mt-4 text-xs font-semibold text-blue-600 flex items-center gap-1 hover:underline">
              Ver detalle
              <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 6.414V16a1 1 0 11-2 0V6.414L5.707 8.707A1 1 0 114.293 7.293l4-4z" clip-rule="evenodd" />
              </svg>
            </button>
          </article>
        </div>
        <div v-else class="flex flex-col items-center justify-center gap-2 text-gray-500 py-12">
          <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 8c1.657 0 3-1.567 3-3.5S13.657 1 12 1 9 2.567 9 4.5 10.343 8 12 8zM5 22v-2a7 7 0 1114 0v2" />
          </svg>
          <p class="text-sm">Este paciente aún no tiene documentos cargados.</p>
        </div>
      </section>
  
      <!-- Modal -->
      <div
        v-if="modalActivo"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-4 py-8"
      >
        <div class="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden">
          <div class="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
            <div>
              <p class="text-sm font-semibold text-gray-900">Vista previa</p>
              <p class="text-xs text-gray-500 truncate max-w-[250px]">{{ documentoSeleccionado.nombre }}</p>
            </div>
            <button
              @click="cerrarModal"
              class="text-gray-500 hover:text-gray-900 rounded-full p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span class="sr-only">Cerrar</span>
              ✕
            </button>
          </div>

          <div class="grid md:grid-cols-[2fr,1fr]">
            <div class="bg-gray-100 p-4 min-h-[320px] flex items-center justify-center">
              <img
                v-if="esImagen(documentoSeleccionado.nombre)"
                :src="documentoSeleccionado.url"
                alt="Documento"
                class="max-h-[70vh] w-auto rounded-lg shadow-md"
              />
              <iframe
                v-else-if="esPDF(documentoSeleccionado.nombre)"
                :src="documentoSeleccionado.url"
                class="w-full h-[70vh] rounded-lg bg-white shadow-inner"
              ></iframe>
              <p v-else class="text-sm text-gray-600">
                No se puede mostrar vista previa de este tipo de archivo.
              </p>
            </div>

            <div class="p-6 flex flex-col justify-between gap-4">
              <div class="space-y-3">
                <p class="text-sm font-semibold text-gray-900">Detalles del archivo</p>
                <div class="text-xs text-gray-600 space-y-1">
                  <p><span class="font-semibold text-gray-800">Nombre:</span> {{ documentoSeleccionado.nombre }}</p>
                  <p><span class="font-semibold text-gray-800">Tipo:</span> {{ obtenerExtension(documentoSeleccionado.nombre) }}</p>
                </div>
              </div>
              <div class="flex flex-col gap-2">
                <a
                  :href="documentoSeleccionado.url"
                  target="_blank"
                  download
                  class="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition"
                >
                  Descargar archivo
                </a>
                <button
                  @click="abrirDocumentoNuevaPestana"
                  class="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition"
                >
                  Abrir en nueva pestaña
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { storage, db } from "@/firebase";
  import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
  import { collection, addDoc } from "firebase/firestore";

  export default {
    name: "DocumentosPacienteAdmin",
    props: {
      paciente: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        documentos: [],
        modalActivo: false,
        documentoSeleccionado: {},
        archivoSeleccionado: null,
        subiendoDocumento: false,
        mensajeSubida: "",
        tipoMensaje: "success"
      };
    },
    watch: {
      paciente: {
        handler() {
          this.cargarDocumentos();
        },
        immediate: true
      }
    },
    methods: {
      abrirModal(doc) {
        this.documentoSeleccionado = doc;
        this.modalActivo = true;
      },
      cerrarModal() {
        this.modalActivo = false;
        this.documentoSeleccionado = {};
      },
      esImagen(nombre) {
        return /\.(jpe?g|png|gif|webp)$/i.test(nombre);
      },
      esPDF(nombre) {
        return /\.pdf$/i.test(nombre);
      },
      manejarArchivo(event) {
        this.archivoSeleccionado = event.target.files[0] || null;
        this.mensajeSubida = "";
      },
      limpiarArchivo() {
        this.archivoSeleccionado = null;
        if (this.$refs.archivoInput) {
          this.$refs.archivoInput.value = "";
        }
      },
      obtenerExtension(nombre = "") {
        const partes = nombre.split(".");
        return partes.length > 1 ? partes.pop().toUpperCase() : "DESCONOCIDO";
      },
      abrirDocumentoNuevaPestana() {
        if (this.documentoSeleccionado?.url) {
          window.open(this.documentoSeleccionado.url, "_blank");
        }
      },
      async subirDocumento() {
        const archivo = this.archivoSeleccionado;
        const pacienteId = this.paciente?.uid || this.paciente?.id;

        if (!archivo || !pacienteId) {
          this.mostrarMensaje("error", "Selecciona un archivo válido.");
          return;
        }

        this.subiendoDocumento = true;
        this.mostrarMensaje("", "");

        try {
          const archivoRef = storageRef(storage, `documentos/${pacienteId}/${archivo.name}`);
          await uploadBytes(archivoRef, archivo);
          const url = await getDownloadURL(archivoRef);

          await addDoc(collection(db, `pacientes/${pacienteId}/documentos`), {
            nombre: archivo.name,
            url,
            timestamp: new Date()
          });

          this.mostrarMensaje("success", "Documento subido correctamente.");
          this.archivoSeleccionado = null;
          if (this.$refs.archivoInput) {
            this.$refs.archivoInput.value = "";
          }
          await this.cargarDocumentos();
        } catch (error) {
          console.error("Error subiendo documento:", error);
          this.mostrarMensaje("error", "No se pudo subir el documento.");
        } finally {
          this.subiendoDocumento = false;
        }
      },
      async cargarDocumentos() {
        if (!this.paciente || (!this.paciente.id && !this.paciente.uid)) return;
    
        const uid = this.paciente.uid || this.paciente.id;
    
        try {
          const response = await fetch("https://us-central1-nat-spa.cloudfunctions.net/getDocumentosPaciente", {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({ pacienteId: uid })
          });
    
          if (!response.ok) {
            throw new Error(`Error HTTP: ${response.status}`);
          }
    
          const data = await response.json();
          console.log("Documentos cargados:", data);
          this.documentos = data;
        } catch (error) {
          console.error("Error al obtener documentos:", error);
        }
      },
      mostrarMensaje(tipo, texto) {
        this.tipoMensaje = tipo;
        this.mensajeSubida = texto;
      }
    }
  };
  </script>
  
